import { useState, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, AlertTriangle, User, MapPin, Landmark, Wheat, CreditCard, FileCheck } from 'lucide-react';

const SECTIONS = [
  { key: 'personal', label: 'Personal Details', icon: User, fields: ['farmer_name', 'date_of_birth', 'contact_number', 'aadhaar_number', 'farming_experience_years'] },
  { key: 'location', label: 'Location', icon: MapPin, fields: ['residential_state', 'residential_district', 'residential_taluk', 'residential_village'] },
  { key: 'agricultural', label: 'Agricultural', icon: Wheat, fields: ['total_land_holding_size_acres', 'land_area_hectares', 'primary_crop_type', 'crop_diversification_index'] },
  { key: 'financial', label: 'Financial', icon: Landmark, fields: ['annual_agricultural_income', 'total_outstanding_debt', 'avg_bank_balance_6mo', 'bank_account_vintage_years'] },
  { key: 'credit_history', label: 'Credit History', icon: CreditCard, fields: ['repayment_track_record_pct', 'prior_default_history', 'full_repayment'] },
];

const FIELD_LABELS = {
  farmer_name: 'Farmer Name', date_of_birth: 'Date of Birth', contact_number: 'Contact',
  aadhaar_number: 'Aadhaar', farming_experience_years: 'Experience (Years)',
  residential_state: 'State', residential_district: 'District', residential_taluk: 'Block/Taluk', residential_village: 'Village',
  total_land_holding_size_acres: 'Total Land (Acres)', land_area_hectares: 'Land (Hectares)',
  primary_crop_type: 'Primary Crop', crop_diversification_index: 'Crop Diversification',
  annual_agricultural_income: 'Annual Income', total_outstanding_debt: 'Outstanding Debt',
  avg_bank_balance_6mo: 'Avg Bank Balance (6m)', bank_account_vintage_years: 'Account Vintage (Yrs)',
  repayment_track_record_pct: 'Repayment Track (%)', prior_default_history: 'Prior Defaults', full_repayment: 'Full Repayment',
};

const ReviewProfile = ({ farmer, assessment, previousAssessment, onUpdateAssessment }) => {
  const [confirmations, setConfirmations] = useState({});

  const changedFields = useMemo(() => {
    const changes = new Set();
    (assessment?.changed_fields || []).forEach(cf => changes.add(cf.field));
    return changes;
  }, [assessment?.changed_fields]);

  const prevSnapshot = previousAssessment?.farmer_profile_snapshot || {};

  const toggleSection = (sectionKey) => {
    const next = { ...confirmations, [sectionKey]: !confirmations[sectionKey] };
    setConfirmations(next);

    // Update assessment
    const sectionsConfirmed = {};
    Object.entries(next).forEach(([k, v]) => {
      sectionsConfirmed[k] = { confirmed_no_change: v, confirmed_at: new Date().toISOString() };
    });
    onUpdateAssessment({ sections_confirmed: sectionsConfirmed });
  };

  const formatValue = (val) => {
    if (val === null || val === undefined) return 'N/A';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'number') return val.toLocaleString();
    return String(val);
  };

  const allConfirmed = SECTIONS.every(s => confirmations[s.key]);

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1" data-testid="review-profile-title">Review Farmer Profile</h3>
        <p className="text-sm text-gray-500">
          Review prefilled data from previous cycle. Toggle "No Change" to confirm sections, or note changes.
        </p>
      </div>

      {/* Prefilled from badge */}
      {previousAssessment && (
        <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 rounded-md px-3 py-2 border border-blue-200">
          <FileCheck className="h-4 w-4" />
          Prefilled from Cycle {previousAssessment.cycle_number} ({previousAssessment.scoring_date?.slice(0, 10) || 'N/A'})
        </div>
      )}

      {/* Sections */}
      {SECTIONS.map(section => {
        const Icon = section.icon;
        const isConfirmed = confirmations[section.key];
        const sectionChanges = (assessment?.changed_fields || []).filter(cf => cf.section === section.key);

        return (
          <Card
            key={section.key}
            className={`transition-all ${isConfirmed ? 'border-green-300 bg-green-50/30' : ''}`}
            data-testid={`section-${section.key}`}
          >
            <CardContent className="pt-4">
              {/* Section Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-gray-600" />
                  <span className="font-semibold text-sm text-gray-800">{section.label}</span>
                  {sectionChanges.length > 0 && (
                    <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">
                      {sectionChanges.length} changed
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">No Change</span>
                  <Switch
                    checked={isConfirmed}
                    onCheckedChange={() => toggleSection(section.key)}
                    data-testid={`toggle-${section.key}`}
                  />
                  {isConfirmed && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                </div>
              </div>

              {/* Fields Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {section.fields.map(field => {
                  const currentVal = farmer?.[field];
                  const prevVal = prevSnapshot[field];
                  const isChanged = changedFields.has(field);

                  return (
                    <div key={field} className="flex justify-between items-baseline py-1 border-b border-gray-100 last:border-0">
                      <span className="text-xs text-gray-500">{FIELD_LABELS[field] || field}</span>
                      <div className="text-right">
                        <span className={`text-xs font-medium ${isChanged ? 'text-amber-700' : 'text-gray-900'}`}>
                          {formatValue(currentVal)}
                        </span>
                        {isChanged && prevVal !== undefined && (
                          <div className="text-[10px] text-gray-400 line-through">{formatValue(prevVal)}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Summary */}
      <div className={`p-3 rounded-md text-sm ${allConfirmed ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
        {allConfirmed ? (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            All sections confirmed. Proceed to next step.
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {SECTIONS.filter(s => !confirmations[s.key]).length} section(s) pending confirmation.
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewProfile;
